import React, { useEffect, useState } from "react";

import { Divider, Modal } from "semantic-ui-react";
import { AppColumn } from "../../UI/Layouts";
import { connect, useSelector } from "react-redux";

import { MessagesList } from "./Components/MessagesList";
import { MessageFooter } from "./Components/MessageFooter";
import { ChatMessageHeader } from "./Components/MessageHeader";

import { closeModal, openModal } from "../../../redux/Modals/ModalActions";
import { UPDATE_ONLINE_STATUS } from "../../../utils/Controller/URLConstants";
import axios from "axios";
import { getUTCTimeInMilliseconds } from "../../../utils/helperFunctions";
import { useResponsive } from "../../UI/ResponsiveContext";
import { showToast } from "../../../redux/Snackbar/SnackbarActions";
import useTheme from "../../../hooks/useTheme";
import { MODAL_CANDIDATE_DETAIL } from "../../Candidate/CandidateDetail/CandidateViewPage";
import { getSessionToken } from "../../../utils/sessionFunctions";
import { getChatProfile } from "../chatProfiles";

const ChatMessageDetailPage = ({
  selectedChatId,
  initialThreadId = null,
  openModal,
  closeModal,
  showToast,
}) => {
  const { isMobile } = useResponsive();
  const theme = useTheme();

  const chatId = selectedChatId;

  // Thread state - lifted here so MessageFooter can access it
  const [activeThreadParentId, setActiveThreadParentId] =
    useState(initialThreadId);

  const handleEnterThread = (parentMessageId) => {
    setActiveThreadParentId(parentMessageId);
  };

  const handleExitThread = () => {
    setActiveThreadParentId(null);
  };

  const { currentUser, chats, appliedJobs } = useSelector(
    (state) => state.auth,
  );
  const currentUserId = currentUser?.uid;

  const currentChatData = chats.find((chat) => chatId === chat.chatId);

  const updateOnlineStatus = async (chatId, onlineValue) => {
    try {
      let headers = {};

      const token = await getSessionToken();

      if (token) {
        headers["x-access-token"] = token;
      }

      const response = await axios.post(
        UPDATE_ONLINE_STATUS,
        {
          chatId,
          onlineValue: onlineValue ? "1" : "0",
          userId: currentUserId,
          token: currentUser.stsTokenManager.accessToken,
          at: `${getUTCTimeInMilliseconds()}`,
        },
        {
          headers,
        },
      );

      if (response.data.status !== 0) {
        console.error("Failed to update online status:", response.data.error);
      }
    } catch (error) {
      console.error("Error updating online status:", error);
    }
  };

  useEffect(() => {
    // Set online status when component mounts
    if (chatId && currentUserId) {
      updateOnlineStatus(chatId, true);
    }

    // Set offline status when component unmounts
    return () => {
      if (chatId && currentUserId) {
        updateOnlineStatus(chatId, false);
      }
    };
  }, [chatId, currentUserId]);

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (chatId && currentUserId) {
        updateOnlineStatus(chatId, !document.hidden);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [chatId, currentUserId]);

  const divId = `chat-page-${chatId}`;

  let chatContainerHeight =
    typeof document !== "undefined" && document.body.clientHeight;

  const chatReference =
    typeof document !== "undefined" && document.getElementById(divId);

  if (chatReference) {
    chatContainerHeight = chatReference.clientWidth;
  }

  const chatContent = (() => {
    if (chatId && currentChatData && currentUserId) {
      // Match application to chat using recruiter UIDs from profilesData
      const recruiters = Object.values(
        currentChatData.profilesData || {},
      ).filter((p) => p.role === "recruiter");
      const chatContactUids = recruiters.map((r) => r.userId);

      const candidateData =
        appliedJobs?.find(
          (job) =>
            job.jobId === currentChatData.metaData?.currentChatJobData?.jobId,
        ) ??
        appliedJobs?.find((job) => {
          const appContactUids = job.contacts?.uids ?? [];
          return (
            currentChatData.metaData?.workplaceId &&
            job.workplaceId === currentChatData.metaData?.workplaceId &&
            appContactUids.some((uid) => chatContactUids.includes(uid))
          );
        }) ??
        appliedJobs?.find((job) => {
          const appContactUids = job.contacts?.uids ?? [];
          return (
            appContactUids.length > 0 &&
            chatContactUids.length > 0 &&
            appContactUids.some((uid) => chatContactUids.includes(uid))
          );
        });

      // Resolve contact person's workplace name from profilesData or metaData
      const otherUserId = Object.values(
        currentChatData.profilesData || {},
      ).find((e) => currentUserId !== e.userId)?.userId;
      const otherUserProfile = getChatProfile(currentChatData, otherUserId);
      const contactWorkplaceName =
        otherUserProfile?.workplaceName ||
        currentChatData.metaData?.workplaceName;

      return (
        <AppColumn
          id={divId}
          key={`${chatId}-${initialThreadId || ""}`}
          mainAxisAlignment={"space-between"}
          style={{
            borderLeft: !isMobile && `1px solid ${theme.colors.lightGrey}`,
            height: "90vh",
            width: isMobile ? "100vw" : "100%",
            maxWidth: !isMobile && "calc(100vw - 38vw - 2rem)",
          }}
        >
          {!initialThreadId && !activeThreadParentId && (
            <>
              <ChatMessageHeader
                currentChatData={currentChatData}
                currentUser={currentUser}
                contactWorkplaceName={contactWorkplaceName}
                onClick={
                  candidateData
                    ? () => {
                        openModal(MODAL_CANDIDATE_DETAIL, {
                          candidateData: candidateData,
                          jobId:
                            candidateData.jobDetails?.jobId ??
                            candidateData.jobId,
                        });
                      }
                    : undefined
                }
                closeModal={closeModal}
                openModal={openModal}
              />
              <Divider fitted />
            </>
          )}
          <MessagesList
            chats={chats}
            currentUser={currentUser}
            selectedChatId={chatId}
            chatContainerHeight={chatContainerHeight}
            openModal={openModal}
            activeThreadParentId={activeThreadParentId}
            onEnterThread={handleEnterThread}
            onExitThread={handleExitThread}
            isLockedToThread={!!initialThreadId}
          />
          <MessageFooter
            selectedChatId={chatId}
            currentChatData={currentChatData}
            currentUser={currentUser}
            showToast={showToast}
            openModal={openModal}
            parentMessageId={activeThreadParentId}
            closeModal={closeModal}
          />
        </AppColumn>
      );
    } else {
      return <div></div>;
    }
  })();

  if (isMobile) {
    return (
      <Modal
        // basic
        key={selectedChatId}
        centered={true}
        open={true}
        closeOnEscape={true}
        closeOnDimmerClick={true}
        onClose={() => {
          closeModal();
        }}
        style={{
          margin: "0",
          padding: "0",
          top: "0",
          left: "0",
          right: "0",
          bottom: "0",
          overflow: "hidden",
          position: "fixed",
          height:
            typeof document !== "undefined"
              ? document.body.clientHeight
              : "100vh",
          width: isMobile ? "100vw" : "60%",
          borderRadius: "0px",
        }}
      >
        {chatContent}
      </Modal>
    );
  } else {
    return chatContent;
  }
};

export default connect(null, { openModal, closeModal, showToast })(
  ChatMessageDetailPage,
);

export const MODAL_OPEN_MESSAGE_DETAIL = "ChatMessageDetailPage";
